import React from 'react';
import { Parallax } from 'react-scroll-parallax';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import FloatingElements from './FloatingElements';

const FeaturesSection = styled.section`
  background-image: url("/images/fondo2.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 100px 0;
  position: relative;
  overflow: hidden;
`;

const FeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled(motion.h2)`
  font-family: "BRLNSDB";
  font-size: 3rem;
  color: rgb(255, 255, 255);
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  display: inline-block;

   &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  padding: 2rem;
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
`;

const FeatureTitle = styled.h3`
  font-family: "BRLNSDB";
  font-size: 1.5rem;
  color: #333;
  transition: color 0.3s ease;
`;

const FeatureDescription = styled.p`
  font-family: "Montserrat";
  color: #666;
  line-height: 1.6;
  transition: color 0.3s ease;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 50%, transparent 70%);
    opacity: 0;
    transform: scale(0.5);
    transition: all 0.5s ease;
  }

  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(102, 126, 234, 0.2);

    &:before {
      transform: scaleX(1);
    }

    &:after {
      opacity: 1;
      transform: scale(1);
    }

    ${FeatureIcon} {
      transform: scale(1.1) rotate(5deg);
      background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
      box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
    }

    ${FeatureTitle} {
      color: #667eea;
    }

    ${FeatureDescription} {
      color: #764ba2;
    }
  }
`;

const Features = () => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    const features = [
        {
            icon: '📚',
            title: 'Asignaturas',
            description: 'Contenidos educativos organizados por áreas del conocimiento, alineados con los DBA para cada grado académico.',
        },
        {
            icon: '🔄',
            title: 'Módulos Transversales',
            description: 'Recursos educativos que integran diversas disciplinas, fortaleciendo el aprendizaje integral y contextualizado.',
        },
        {
            icon: '📝',
            title: 'Entrenamiento ICFES',
            description: 'Material de preparación para las pruebas estandarizadas, con simulacros y estrategias de resolución.',
        },
        {
            icon: '🎮',
            title: 'Juegos Educativos',
            description: 'Actividades interactivas que refuerzan el aprendizaje de manera dinámica y divertida.',
        },
        {
            icon: '🌍',
            title: 'Módulo Etnoeducativo',
            description: 'Espacio dedicado al reconocimiento y fortalecimiento de las identidades culturales en el proceso educativo.',
        },
        {
            icon: '📖',
            title: 'Diccionario',
            description: 'Herramienta de consulta de términos clave en diversas áreas del conocimiento para enriquecer el aprendizaje.',
        },
    ];

    return (
        <FeaturesSection id="features">
            <FloatingElements section="features" />
            <Parallax translateY={[-20, 20]}>
                <FeaturesContainer ref={ref}>
                    <Title
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        Características Principales
                    </Title>

                    <FeaturesGrid>
                        {features.map((feature, index) => (
                            <Parallax
                                key={index}
                                translateY={[-20, 20]}
                                scale={[0.95, 1.05]}
                                rotate={[-2, 2]}
                                easing="easeInQuad"
                                speed={10}
                            >
                                <FeatureCard
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={inView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    whileHover={{
                                        scale: 1.02,
                                        transition: { duration: 0.3 }
                                    }}
                                >
                                    <FeatureIcon>{feature.icon}</FeatureIcon>
                                    <FeatureTitle>{feature.title}</FeatureTitle>
                                    <FeatureDescription>{feature.description}</FeatureDescription>
                                </FeatureCard>
                            </Parallax>
                        ))}
                    </FeaturesGrid>
                </FeaturesContainer>
            </Parallax>
        </FeaturesSection>
    );
};

export default Features; 